import React, { useEffect, useRef, useState } from "react";
import { Check, ChevronDown, ExternalLink, Leaf, MapPin, Phone, ShieldCheck, Star, UserCheck } from "lucide-react";
import { business } from "./data/business.js";
import { baseFaqs, homeFaqs, localFaqs } from "./data/faqs.js";
import { findPage, homePage, localPages, servicePages } from "./data/seoPages.js";
import "./styles.css";

const googleBusinessUrl = business.googleBusinessUrl;
const squeegeeSrc = business.squeegeeImage;

const customerReviews = [
  {
    author: "Julie B",
    authorUri: "https://www.google.com/maps/contrib/112182093151313905868/reviews?hl=fr-FR",
    rating: 5,
    sourceLabel: "Avis publié sur Google",
    text: "Toutes les semaines une grande satisfaction de retrouver ma maison impeccable grâce à Alexandre et Anna. C est une aide précieuse dans mon quotidien. Je recommande à 100%."
  },
  {
    author: "Cécile tutu (Tutucecile)",
    authorUri: "https://www.google.com/maps/contrib/107330478148623053093/reviews?hl=fr-FR",
    rating: 5,
    sourceLabel: "Avis publié sur Google",
    text: "Toujours de bonne humeur, un travail impeccable et soigné, tres minutieux avec une attention du détail et surtout ultra fiable et ponctuel, je recommande vivement les services d’Alexandre qui travaille avec nous depuis plusieurs mois maintenant!"
  },
  {
    author: "PERLE hama",
    authorUri: "https://www.google.com/maps/contrib/108929771046169683961/reviews?hl=fr-FR",
    rating: 5,
    sourceLabel: "Avis publié sur Google",
    text: "Très satisfaite de la qualité de travail de ménage d’Ana et d’Alexandre. Ponctuels, minutieux, attentionnés, je les recommande à 1000% !"
  }
];

const initialContactForm = {
  name: "",
  email: "",
  phone: "",
  city: "",
  surface: "",
  preferredSlots: "",
  privacyAccepted: false,
  website: ""
};

export default function App({ path }) {
  const resolvedPath = path || (typeof window !== "undefined" ? window.location.pathname : "/");
  const page = findPage(resolvedPath);

  return (
    <main className="page-shell">
      <Header />
      {page.kind === "home" && <HomePage page={page} />}
      {page.kind === "service" && <ServicePage page={page} />}
      {page.kind === "local" && <LocalCityPage page={page} />}
      {page.kind === "privacy" && <PrivacyPage page={page} />}
      {page.kind === "not-found" && <NotFoundPage />}
      <Footer />
    </main>
  );
}

function HomePage({ page }) {
  return (
    <>
      <Hero page={page} />
      <TrustBadges />
      <HomeIntro />
      <Services />
      <TaxCreditSection />
      <MethodSection />
      <ContactForm />
      <Testimonials />
      <ServiceArea />
      <Faq items={homeFaqs} />
    </>
  );
}

function ServicePage({ page }) {
  return (
    <>
      <InternalHero page={page} eyebrow="Ménage hebdomadaire Arklis" />
      <Breadcrumbs items={page.breadcrumbs} />
      <section className="section content-section">
        <div className="section-heading">
          <h2>Une formule unique, pensée pour votre quotidien</h2>
          <p>{page.summary}</p>
        </div>
        <div className="content-grid">
          <article>
            <h3>Ce que le passage hebdomadaire peut inclure</h3>
            <ul className="clean-list">
              {page.details.map((item) => <li key={item}>{item}</li>)}
            </ul>
          </article>
          <article>
            <h3>Pour quels foyers ?</h3>
            <ul className="clean-list">
              {page.useCases.map((item) => <li key={item}>{item}</li>)}
            </ul>
          </article>
        </div>
      </section>
      <TaxCreditSection compact />
      <MethodSection compact />
      <section className="section link-section">
        <div className="section-heading">
          <h2>Zones desservies autour de Val d'Europe</h2>
          <p>Retrouvez les villes où Arklis propose son service de ménage hebdomadaire à domicile.</p>
        </div>
        <LinkCloud links={localPages.map((local) => ({ href: local.path, label: `ménage hebdomadaire à ${local.city}` }))} />
      </section>
      <ContactForm />
      <Testimonials />
      <Faq items={page.faq} />
      <FinalCta />
    </>
  );
}

function LocalCityPage({ page }) {
  const nearbyLinks = page.nearby
    .map((city) => localPages.find((local) => local.city === city))
    .filter(Boolean);

  return (
    <>
      <InternalHero page={page} eyebrow={`Zone d'intervention · ${business.areaName}`} />
      <Breadcrumbs items={page.breadcrumbs} />
      <section className="section content-section">
        <div className="section-heading">
          <h2>Un service de ménage chaque semaine à {page.city}</h2>
          <p>{page.intro}</p>
        </div>
        <div className="content-grid">
          <article>
            <h3>Une organisation régulière</h3>
            <p>{page.angle}</p>
          </article>
          <article>
            <h3>Offre disponible</h3>
            <ul className="clean-list">
              {servicePages.map((service) => (
                <li key={service.path}>
                  <a href={service.path}>{service.shortTitle}</a>
                </li>
              ))}
            </ul>
          </article>
        </div>
      </section>
      <ContactForm />
      <section className="section link-section">
        <div className="section-heading">
          <h2>Villes proches desservies</h2>
          <p>Arklis intervient aussi chaque semaine dans les villes voisines du secteur Val d'Europe.</p>
        </div>
        <LinkCloud links={nearbyLinks.map((local) => ({ href: local.path, label: `ménage à domicile à ${local.city}` }))} />
      </section>
      <Testimonials />
      <Faq items={localFaqs(page.city)} />
      <FinalCta />
    </>
  );
}

function PrivacyPage({ page }) {
  return (
    <>
      <section className="internal-hero">
        <p className="eyebrow">Confidentialité</p>
        <h1>{page.h1}</h1>
        <p>Cette page explique comment les informations transmises via le formulaire Arklis sont utilisées.</p>
      </section>
      <Breadcrumbs items={[{ label: "Accueil", path: "/" }, { label: "Politique de confidentialité", path: page.path }]} />
      <section className="section content-section legal-content">
        <div className="section-heading">
          <h2>Données collectées</h2>
          <p>
            Les données transmises dans le formulaire peuvent comprendre : nom, email, téléphone, ville,
            surface approximative du logement et créneaux souhaités.
          </p>
        </div>
        <div className="content-grid">
          <article>
            <h3>Finalité</h3>
            <p>
              Ces informations sont utilisées uniquement pour répondre à votre demande, prendre contact avec vous,
              préparer un éventuel devis personnalisé et organiser un échange sur une prestation hebdomadaire.
            </p>
          </article>
          <article>
            <h3>Absence de réutilisation</h3>
            <p>
              Les informations ne sont pas utilisées pour une newsletter, une prospection ultérieure sans demande,
              une revente, une location, un enrichissement de base marketing ou un profilage publicitaire.
            </p>
          </article>
          <article>
            <h3>Transmission et conservation</h3>
            <p>
              Le site ne sauvegarde pas les formulaires dans une base de données. Les informations sont transmises
              à Arklis par email et peuvent transiter par le prestataire technique utilisé pour l'envoi.
              Elles sont supprimées par Arklis après traitement de la demande et lorsqu'aucun suivi n'est plus nécessaire.
              Si une relation contractuelle débute, certaines données nécessaires à la gestion du contrat, à la facturation
              ou aux obligations légales peuvent être conservées séparément pendant les durées imposées par la loi.
            </p>
          </article>
          <article>
            <h3>Base juridique et destinataires</h3>
            <p>
              Le traitement est nécessaire pour répondre à une demande formulée par l'utilisateur et prendre,
              à sa demande, des mesures précontractuelles. Les destinataires sont limités aux personnes autorisées
              chez Arklis et au prestataire technique utilisé pour transmettre l'email.
            </p>
          </article>
          <article>
            <h3>Vos droits</h3>
            <p>
              Vous pouvez demander l'accès, la rectification, l'effacement ou la limitation de vos données, ainsi que
              vous opposer au traitement lorsque cela s'applique. Vous pouvez aussi déposer une réclamation auprès de la CNIL.
            </p>
          </article>
          <article>
            <h3>Contact</h3>
            <p>
              Pour exercer vos droits ou poser une question sur vos données, vous pouvez écrire à
              {" "}<a href={`mailto:${business.privacyEmail}`}>{business.privacyEmail}</a>.
            </p>
          </article>
        </div>
      </section>
      <FinalCta />
    </>
  );
}

function NotFoundPage() {
  return (
    <>
      <section className="internal-hero not-found-hero">
        <p className="eyebrow">Arklis</p>
        <h1>Page introuvable</h1>
        <p>La page demandée n'existe pas ou a été déplacée. Vous pouvez revenir à l'accueil ou contacter Arklis.</p>
        <div className="hero-actions">
          <a className="button button-primary button-large" href="/">Retour à l'accueil</a>
          <a className="button button-secondary button-large" href="/#contact">Être recontacté</a>
        </div>
      </section>
    </>
  );
}

function Header() {
  return (
    <header className="site-header">
      <a className="brand" href="/" aria-label="Arklis accueil">
        <img src={business.logoImage} alt="Arklis" width="1200" height="360" />
      </a>
      <nav className="nav-links" aria-label="Navigation principale">
        <a href="/">Accueil</a>
        <a href="/femme-de-menage-val-deurope/">Ménage hebdomadaire</a>
        <a href="/#zone">Zones d'intervention</a>
        <a href="/#avis">Avis</a>
        <a href="/#contact">Contact</a>
      </nav>
      <details className="mobile-menu">
        <summary>Menu</summary>
        <nav aria-label="Navigation mobile">
          <a href="/">Accueil</a>
          <a href="/femme-de-menage-val-deurope/">Ménage hebdomadaire</a>
          <a href="/#zone">Zones d'intervention</a>
          <a href="/#avis">Avis</a>
          <a href="/#contact">Contact</a>
        </nav>
      </details>
      <div className="header-actions">
        <a className="phone-link" href={`tel:${business.phoneHref}`}>
          <Phone size={16} aria-hidden="true" />
          <span>{business.phoneDisplay}</span>
        </a>
        <a className="button button-primary" href="/#contact">Être recontacté</a>
      </div>
    </header>
  );
}

function Hero({ page = homePage }) {
  return (
    <section id="top" className="hero">
      <div className="hero-copy">
        <p className="eyebrow">Ménage hebdomadaire · Val d'Europe</p>
        <h1>{page.h1}</h1>
        <p className="hero-text">
          Arklis entretient votre domicile chaque semaine à Serris, Chessy, Montévrain et dans les villes voisines
          de Val d'Europe. Un service régulier, fiable et pensé pour s'intégrer durablement à votre quotidien.
        </p>
        <div className="hero-actions">
          <a className="button button-primary button-large" href="#contact">Être recontacté</a>
          <a className="button button-secondary button-large" href={`tel:${business.phoneHref}`}>Appeler le {business.phoneDisplay}</a>
        </div>
      </div>
      <div className="hero-media" aria-label="Intérieur lumineux nettoyé">
        <img src={business.heroImage} alt="Intérieur lumineux entretenu chaque semaine par Arklis" width="1536" height="1024" fetchPriority="high" />
      </div>
      <HeroWipeIntro />
    </section>
  );
}

function InternalHero({ page, eyebrow }) {
  return (
    <section className="internal-hero">
      <p className="eyebrow">{eyebrow}</p>
      <h1>{page.h1}</h1>
      <p>{page.description}</p>
      <div className="hero-actions">
        <a className="button button-primary button-large" href="/#contact">Être recontacté</a>
        <a className="button button-secondary button-large" href={`tel:${business.phoneHref}`}>Appeler le {business.phoneDisplay}</a>
      </div>
    </section>
  );
}

function Breadcrumbs({ items = [] }) {
  return (
    <nav className="breadcrumbs" aria-label="Fil d'Ariane">
      {items.map((item, index) => (
        <React.Fragment key={item.path}>
          {index > 0 && <span aria-hidden="true">/</span>}
          <a href={item.path}>{item.label}</a>
        </React.Fragment>
      ))}
    </nav>
  );
}

function HomeIntro() {
  return (
    <section className="section content-section">
      <div className="section-heading">
        <h2>Arklis, service de ménage hebdomadaire à Val d'Europe</h2>
        <p>
          Arklis accompagne les particuliers qui veulent déléguer l'entretien courant de leur logement dans un cadre régulier.
          L'offre repose sur un passage chaque semaine, avec une organisation suivie et adaptée aux habitudes du foyer.
        </p>
      </div>
      <div className="content-grid">
        <article>
          <h3>Un entretien courant du domicile</h3>
          <p>Sols, poussière, cuisine, sanitaires et surfaces courantes sont abordés selon les priorités définies avec vous.</p>
        </article>
        <article>
          <h3>Une relation régulière</h3>
          <p>Le rythme hebdomadaire permet de construire un service durable, plus lisible et plus confortable au quotidien.</p>
        </article>
      </div>
    </section>
  );
}

function MethodSection({ compact = false }) {
  const steps = [
    ["Demande", "Vous indiquez votre ville, votre logement et les créneaux qui vous conviendraient."],
    ["Échange", "Arklis vous recontacte pour comprendre vos priorités et le cadre hebdomadaire souhaité."],
    ["Organisation", "Le passage chaque semaine est préparé selon les habitudes du foyer."],
    ["Suivi", "La relation s'inscrit dans la durée pour garder un domicile entretenu régulièrement."]
  ];

  return (
    <section className={`section method-section ${compact ? "compact-section" : ""}`}>
      <div className="section-heading">
        <h2>La méthode Arklis</h2>
        <p>Un parcours simple pour installer une organisation de ménage hebdomadaire claire et suivie.</p>
      </div>
      <div className="method-grid">
        {steps.map(([title, text], index) => (
          <article className="method-card" key={title}>
            <span>{index + 1}</span>
            <h3>{title}</h3>
            <p>{text}</p>
          </article>
        ))}
      </div>
    </section>
  );
}

function LinkCloud({ links }) {
  return (
    <div className="link-cloud">
      {links.map((link) => <a href={link.href} key={`${link.href}-${link.label}`}>{link.label}</a>)}
    </div>
  );
}

function FinalCta() {
  return (
    <section className="section final-cta">
      <h2>Vous recherchez un ménage chaque semaine ?</h2>
      <p>Échangeons sur votre logement, vos priorités et le créneau hebdomadaire qui vous conviendrait.</p>
      <div className="hero-actions">
        <a className="button button-primary button-large" href="/#contact">Être recontacté</a>
        <a className="button button-secondary button-large" href={`tel:${business.phoneHref}`}>Nous appeler</a>
      </div>
    </section>
  );
}

function HeroWipeIntro() {
  const [isVisible, setIsVisible] = useState(true);
  const [isReady, setIsReady] = useState(false);
  const [progress, setProgress] = useState(0);
  const frameRef = useRef(null);
  const delayRef = useRef(null);
  const decodeTimeoutRef = useRef(null);

  useEffect(() => {
    const motionQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    if (motionQuery.matches) {
      setIsVisible(false);
      return undefined;
    }

    let isCancelled = false;
    const image = new Image();
    image.src = squeegeeSrc;

    async function runIntro() {
      try {
        if (image.decode) {
          await Promise.race([
            image.decode(),
            new Promise((resolve) => {
              decodeTimeoutRef.current = window.setTimeout(resolve, 1200);
            })
          ]);
        }
      } catch {
        // The animation can still run if decode() is unavailable or rejected.
      }

      if (decodeTimeoutRef.current) {
        window.clearTimeout(decodeTimeoutRef.current);
        decodeTimeoutRef.current = null;
      }

      if (isCancelled) return;
      setIsReady(true);

      delayRef.current = window.setTimeout(() => {
        const duration = 2050;
        const start = performance.now();
        const endProgress = 118;

        const tick = (now) => {
          const elapsed = now - start;
          const t = Math.min(1, elapsed / duration);
          const eased = t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
          setProgress(eased * endProgress);

          if (t < 1) {
            frameRef.current = window.requestAnimationFrame(tick);
          } else {
            setIsVisible(false);
          }
        };

        frameRef.current = window.requestAnimationFrame(tick);
      }, 220);
    }

    runIntro();

    return () => {
      isCancelled = true;
      if (decodeTimeoutRef.current) window.clearTimeout(decodeTimeoutRef.current);
      if (delayRef.current) window.clearTimeout(delayRef.current);
      if (frameRef.current) window.cancelAnimationFrame(frameRef.current);
    };
  }, []);

  if (!isVisible) return null;

  return (
    <div
      className={`hero-wipe-intro ${isReady ? "is-ready" : ""}`}
      style={{ "--hero-wipe-progress": progress }}
      aria-hidden="true"
    >
      <div className="hero-fog" />
      <div className="hero-squeegee-track">
        <img className="hero-squeegee" src={squeegeeSrc} alt="" aria-hidden="true" focusable="false" draggable="false" />
      </div>
    </div>
  );
}

function TrustBadges() {
  const items = [
    { icon: ShieldCheck, title: "Intervention chaque semaine", text: "Organisation hebdomadaire suivie" },
    { icon: UserCheck, title: "50 % de crédit d'impôt*", text: "Sous conditions légales" },
    { icon: Leaf, title: business.cesuLabel, text: "Selon les modalités applicables" }
  ];

  return (
    <section className="trust-strip" aria-label="Garanties Arklis">
      {items.map(({ icon: Icon, title, text }) => (
        <article className="trust-item" key={title}>
          <span className="icon-pill"><Icon size={20} aria-hidden="true" /></span>
          <span>
            <strong>{title}</strong>
            <small>{text}</small>
          </span>
        </article>
      ))}
    </section>
  );
}

function Services() {
  return (
    <section id="services" className="section">
      <div className="section-heading">
        <h2>Une formule unique, pensée pour votre quotidien</h2>
        <p>
          Arklis se concentre sur le ménage hebdomadaire afin d'assurer un entretien régulier,
          une meilleure continuité et un service adapté aux habitudes de votre foyer.
        </p>
      </div>
      <div className="service-grid service-grid-single">
        {servicePages.map((service) => (
          <ServiceCard key={service.path} title={service.shortTitle} href={service.path}>
            {service.summary}
          </ServiceCard>
        ))}
      </div>
    </section>
  );
}

function ServiceCard({ title, href, children }) {
  return (
    <article className="service-card">
      <p className="price-label">Chaque semaine</p>
      <h3>{title}</h3>
      <p>{children}</p>
      {href && <a className="text-link" href={href}>Découvrir le service</a>}
    </article>
  );
}

function TaxCreditSection({ compact = false }) {
  return (
    <section className={`section content-section tax-section ${compact ? "compact-section" : ""}`}>
      <div className="section-heading">
        <h2>Un service à la personne avantageux</h2>
        <p>
          Les prestations de ménage à domicile Arklis peuvent ouvrir droit à un crédit d'impôt de 50 % des dépenses supportées,
          sous réserve des conditions légales, des plafonds en vigueur et de l'éligibilité du client.
        </p>
      </div>
      <div className="content-grid">
        <article>
          <h3>{business.cesuLabel}</h3>
          <p>Arklis accepte le paiement en CESU selon les modalités applicables aux prestations de services à la personne.</p>
        </article>
        <article>
          <h3>{business.sapStatus}</h3>
          <p>
            Les prestations sont présentées dans le cadre des services à la personne, avec des avantages soumis
            aux conditions légales applicables.
            {business.sapDeclarationNumber ? ` Numéro de déclaration : ${business.sapDeclarationNumber}.` : ""}
          </p>
        </article>
      </div>
      <p className="legal-note">* Sous réserve des conditions légales, de l'éligibilité du client et des plafonds en vigueur.</p>
    </section>
  );
}

function ContactForm() {
  const [form, setForm] = useState(initialContactForm);
  const [status, setStatus] = useState("idle");
  const [feedback, setFeedback] = useState("");

  const update = (field, value) => setForm((current) => ({ ...current, [field]: value }));

  async function submit(event) {
    event.preventDefault();
    setFeedback("");

    if (!form.privacyAccepted) {
      setStatus("error");
      setFeedback("Veuillez confirmer que vous avez pris connaissance de la politique de confidentialité.");
      return;
    }

    setStatus("submitting");

    try {
      const response = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...form,
          sourcePage: typeof window !== "undefined" ? window.location.href : "/"
        })
      });

      await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(response.status === 503 ? "service_unavailable" : "contact_failed");

      setStatus("success");
      setFeedback("Merci. Votre demande a bien été transmise à Arklis. Nous vous recontacterons prochainement.");
      setForm(initialContactForm);
    } catch (error) {
      setStatus("error");
      const unavailable = error.message === "service_unavailable";
      setFeedback(
        unavailable
          ? `Le formulaire est momentanément indisponible. Vous pouvez nous joindre au ${business.phoneDisplay}.`
          : `L'envoi n'a pas abouti. Vous pouvez nous joindre au ${business.phoneDisplay}.`
      );
    }
  }

  return (
    <section id="contact" className="section quote-section contact-section">
      <span id="devis" className="anchor-compat" aria-hidden="true" />
      <div className="quote-panel">
        <div className="quote-head">
          <h2>Parlons de votre besoin de ménage hebdomadaire</h2>
          <p>Quelques informations nous permettront de vous rappeler et de préparer une proposition adaptée à votre logement.</p>
        </div>

        <form className="quote-body contact-form" onSubmit={submit}>
          <input
            className="honeypot"
            name="website"
            value={form.website}
            onChange={(event) => update("website", event.target.value)}
            autoComplete="off"
            tabIndex="-1"
            aria-hidden="true"
          />

          <div className="form-grid">
            <label>
              <span>Prénom et nom *</span>
              <input name="name" value={form.name} onChange={(event) => update("name", event.target.value)} autoComplete="name" required maxLength="120" />
            </label>
            <label>
              <span>Email *</span>
              <input name="email" type="email" value={form.email} onChange={(event) => update("email", event.target.value)} autoComplete="email" required maxLength="160" />
            </label>
            <label>
              <span>Téléphone *</span>
              <input name="phone" type="tel" value={form.phone} onChange={(event) => update("phone", event.target.value)} autoComplete="tel" required maxLength="40" />
            </label>
            <label>
              <span>Ville *</span>
              <input name="city" value={form.city} onChange={(event) => update("city", event.target.value)} autoComplete="address-level2" required maxLength="120" />
            </label>
            <label>
              <span>Surface approximative</span>
              <input name="surface" value={form.surface} onChange={(event) => update("surface", event.target.value)} autoComplete="off" maxLength="80" />
            </label>
            <label>
              <span>Jours ou créneaux préférés</span>
              <input name="preferredSlots" value={form.preferredSlots} onChange={(event) => update("preferredSlots", event.target.value)} autoComplete="off" maxLength="180" />
            </label>
          </div>

          <label className="privacy-check">
            <input
              type="checkbox"
              checked={form.privacyAccepted}
              onChange={(event) => update("privacyAccepted", event.target.checked)}
              required
            />
            <span>J'ai pris connaissance de la <a href="/politique-de-confidentialite/">politique de confidentialité</a>.</span>
          </label>

          <p className="privacy-note">
            Les informations transmises sont utilisées uniquement pour répondre à votre demande. Elles ne sont ni revendues,
            ni réutilisées à des fins de prospection. <a href="/politique-de-confidentialite/">Consulter notre politique de confidentialité</a>.
          </p>

          <div className="quote-controls contact-controls">
            <button className="button button-primary" type="submit" disabled={status === "submitting"}>
              {status === "submitting" ? "Envoi en cours..." : "Être recontacté"}
            </button>
            <a className="button button-secondary" href={`tel:${business.phoneHref}`}>Appeler {business.phoneDisplay}</a>
          </div>

          <div className={`form-feedback ${status}`} role="status" aria-live="polite">
            {status === "success" && <Check size={18} aria-hidden="true" />}
            {feedback}
          </div>
        </form>
      </div>
    </section>
  );
}

function Testimonials() {
  return (
    <section id="avis" className="section testimonials">
      <div className="review-heading">
        <div>
          <p className="price-label">Avis Google</p>
          <h2>Ce qu'en disent vos voisins</h2>
          <p>Trois témoignages 5 étoiles publiés sur la fiche Google Arklis.</p>
        </div>
        <a className="button button-secondary review-link" href={googleBusinessUrl} target="_blank" rel="noreferrer">
          <span>Voir tous les avis sur Google</span>
          <ExternalLink size={16} aria-hidden="true" />
        </a>
      </div>
      <div className="testimonial-grid google-review-grid">
        {customerReviews.map((review) => (
          <article className="testimonial-card google-review-card" key={review.author}>
            <div className="stars" aria-label={`${review.rating} étoiles`}>
              {Array.from({ length: 5 }).map((_, index) => (
                <Star key={index} size={15} fill={index < Math.round(review.rating) ? "currentColor" : "none"} aria-hidden="true" />
              ))}
            </div>
            <p>"{review.text}"</p>
            <a className="review-author-link" href={review.authorUri} target="_blank" rel="noreferrer">{review.author}</a>
            <span>{review.sourceLabel}</span>
          </article>
        ))}
      </div>
    </section>
  );
}

function ServiceArea() {
  return (
    <section id="zone" className="section area-section">
      <div>
        <h2>Zone couverte</h2>
        <p>Nous intervenons chaque semaine dans le secteur de Val d'Europe.</p>
        <div className="city-list">
          {localPages.map((local) => <a key={local.path} href={local.path}>{local.city}</a>)}
        </div>
      </div>
      <div className="map-card" aria-label="Zone Val d'Europe">
        <MapPin size={36} aria-hidden="true" />
        <span>Val d'Europe</span>
      </div>
    </section>
  );
}

function Faq({ items = baseFaqs }) {
  return (
    <section id="faq" className="section faq-section">
      <h2>Questions fréquentes</h2>
      <div className="faq-list">
        {items.map((item) => (
          <details className="faq-item" key={item.q} open>
            <summary>
              <span>{item.q}</span>
              <ChevronDown size={20} aria-hidden="true" />
            </summary>
            <p>{item.a}</p>
          </details>
        ))}
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="footer">
      <div>
        <strong>Arklis</strong>
        <span>Ménage hebdomadaire à domicile · {business.areaName}, {business.department}</span>
        <span>{business.cesuLabel}</span>
        <div className="footer-agency">
          <span>Site réalisé et administré par notre agence</span>
          <a href="https://smartcrea.fr/" target="_blank" rel="noreferrer" aria-label="Smartcrea">
            <img src="/assets/logo-smartcrea.svg" alt="Smartcrea" loading="lazy" />
          </a>
        </div>
      </div>
      <div className="footer-links">
        <strong>Service</strong>
        {servicePages.map((service) => <a href={service.path} key={service.path}>{service.shortTitle}</a>)}
        <span>Crédit d'impôt conditionnel de 50 %*</span>
      </div>
      <div className="footer-links">
        <strong>Zones</strong>
        {localPages.slice(0, 6).map((local) => <a href={local.path} key={local.path}>{local.city}</a>)}
      </div>
      <div className="footer-links">
        <strong>Contact</strong>
        <a href={`mailto:${business.publicEmail}`}>{business.publicEmail}</a>
        <a href={`tel:${business.phoneHref}`}>{business.phoneDisplay}</a>
        <a href="/#contact">Être recontacté</a>
        <a href="/politique-de-confidentialite/">Politique de confidentialité</a>
        <a href={business.googleBusinessUrl} target="_blank" rel="noreferrer">Fiche Google</a>
      </div>
    </footer>
  );
}
